<section class="st" id="our-strength" #stRoot>
  <!-- decorative layers -->
  <div class="st__deco" aria-hidden="true">
    <span class="st__orb st__orb--1"></span>
    <span class="st__orb st__orb--2"></span>
    <span class="st__mesh"></span>
  </div>

  <div class="container st__inner">
    <!-- ============ intro + pillars ============ -->
    <div class="st__hero">
      <!-- ---------- left: intro ---------- -->
      <div class="st__intro" [@stFade]="heroState()">
        <span class="st__eyebrow">Our Strength</span>

        <h2 class="st__title">
          The Power Behind
          <span class="st__title-line">
            Our <span class="st__title-grad">Performance</span>
          </span>
        </h2>

        <p class="st__lede">
          Key pillars that define our capability, culture, and commitment.
        </p>

        <span class="st__intro-art" aria-hidden="true">
          <i class="st__intro-orb"></i>
          <i class="st__intro-dots"></i>
        </span>
      </div>

      <!-- ---------- right: six pillar cards ---------- -->
      <ul class="st__grid" [@stCards]="heroState()">
        <li
          class="st-card"
          *ngFor="let p of pillars; trackBy: trackById"
          [attr.data-tone]="p.tone"
        >
          <span class="st-card__glow" aria-hidden="true"></span>

          <span class="st-card__ico" aria-hidden="true">
            <svg
              *ngIf="p.icon === 'users'"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <svg
              *ngIf="p.icon === 'expert'"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="11" cy="8" r="4" />
              <path d="M3 21v-1a6 6 0 0 1 6-6h3" />
              <path d="m17 13 1.3 2.7 3 .4-2.2 2.1.5 3-2.6-1.4-2.6 1.4.5-3-2.2-2.1 3-.4z" />
            </svg>
            <svg
              *ngIf="p.icon === 'shield'"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 3 4.5 6v6c0 4.5 3.2 8.3 7.5 9.5 4.3-1.2 7.5-5 7.5-9.5V6z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            <svg
              *ngIf="p.icon === 'agile'"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 12a9 9 0 1 1-9-9" />
              <path d="M21 3v6h-6" />
            </svg>
            <svg
              *ngIf="p.icon === 'code'"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m8 6-6 6 6 6" />
              <path d="m16 6 6 6-6 6" />
            </svg>
            <svg
              *ngIf="p.icon === 'award'"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="9" r="6" />
              <path d="m9 14-1.5 7L12 19l4.5 2L15 14" />
              <path d="m12 6.5.9 1.9 2 .3-1.5 1.4.4 2-1.8-1-1.8 1 .4-2L9.1 8.7l2-.3z" />
            </svg>
          </span>

          <span class="st-card__text">{{ p.text }}</span>
          <span class="st-card__rule" aria-hidden="true"></span>
        </li>
      </ul>
    </div>

    <!-- ============ vision + mission ============ -->
    <div class="st__vm" #stVm [@stPanels]="vmState()">
      <article
        class="st-vm"
        *ngFor="let v of panels; trackBy: trackById"
        [attr.data-tone]="v.tone"
        [class.is-vision]="v.id === 'vision'"
        [class.is-mission]="v.id === 'mission'"
      >
        <span class="st-vm__dots" aria-hidden="true"></span>

        <span class="st-vm__waves" aria-hidden="true">
          <svg viewBox="0 0 620 130" fill="none" stroke="currentColor" stroke-width="0.9" preserveAspectRatio="none">
            <path d="M0 92c62-44 124-58 186-42s124 64 186 64 124-48 186-72" />
            <path d="M0 104c62-44 124-58 186-42s124 64 186 64 124-48 186-72" />
            <path d="M0 116c62-44 124-58 186-42s124 64 186 64 124-48 186-72" />
            <path d="M0 128c62-44 124-58 186-42s124 64 186 64 124-48 186-72" />
          </svg>
        </span>

        <figure class="st-vm__art">
          <span class="st-vm__halo" aria-hidden="true"></span>
          <span class="st-vm__disc">
            <i class="st-vm__glyph" role="img" [attr.aria-label]="v.label + ' ' + v.accent"></i>
          </span>
        </figure>

        <div class="st-vm__body">
          <h3 class="st-vm__title">
            {{ v.label }} <span>{{ v.accent }}</span>
          </h3>
          <span class="st-vm__rule" aria-hidden="true"></span>
          <p class="st-vm__text">{{ v.body }}</p>
        </div>
      </article>
    </div>
  </div>
</section>
